import { apiClient } from './client';
import type { 
  YoutubeVideoListResponse, 
  YoutubeCategory,
  YoutubeQueueData,
  YoutubeQueueItem,
  ReorderQueueParams,
  QueueVideoParams,
  UnqueueVideoParams
} from './types/youtube';
import { ApiClient } from './client';

/**
 * Queue class that provides methods for manipulating the YouTube video queue
 */
export class YoutubeQueue {
  private readonly apiClient: ApiClient;
  public items: YoutubeQueueItem[];

  constructor(apiClient: ApiClient, queueData: YoutubeQueueData) {
    this.apiClient = apiClient;
    this.items = queueData.items || [];
  }

  /**
   * Add a video to the queue
   * @param videoId The YouTube video ID to add
   * @returns Updated Queue instance
   */
  async addVideo(videoId: string): Promise<YoutubeQueue> {
    try {
      const payload: QueueVideoParams = { video_id: videoId };
      const response = await this.apiClient.post<YoutubeQueueData>('/youtube/queue/add-video/', payload);
      return new YoutubeQueue(this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to add video to YouTube queue:', error);
      throw error;
    }
  }

  /**
   * Remove a video from the queue
   * @param itemId The queue item ID to remove
   * @returns Updated Queue instance
   */
  async removeVideo(itemId: number): Promise<YoutubeQueue> {
    try {
      const payload: UnqueueVideoParams = { item: itemId };
      const response = await this.apiClient.post<YoutubeQueueData>('/youtube/queue/remove-video/', payload);
      return new YoutubeQueue(this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to remove video from YouTube queue:', error);
      throw error;
    }
  }

  /**
   * Reorder the queue items
   * @param itemIds Array of item IDs in the desired order
   * @returns Updated Queue instance
   */
  async reorder(itemIds: number[]): Promise<YoutubeQueue> {
    try {
      const payload: ReorderQueueParams = { items: itemIds };
      const response = await this.apiClient.post<YoutubeQueueData>('/youtube/queue/reorder/', payload);
      return new YoutubeQueue(this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to reorder YouTube queue:', error);
      throw error;
    }
  }
}

export const youtube = {

  async getVideos(category_id?: YoutubeCategory): Promise<YoutubeVideoListResponse> {
    try {
      const endpoint = '/youtube/videos/';
      const params = category_id ? { id: category_id } : {};
      console.log(`Calling API: ${endpoint} with params:`, params);
      const response = await apiClient.get<YoutubeVideoListResponse>(endpoint, { params });
      console.log('API response:', response);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch YouTube videos:', error);
      throw error;
    }
  },

  async searchVideos(query: string): Promise<YoutubeVideoListResponse> {
    try {
      if (!query.trim()) {
        console.warn('[YouTube API] Rejected empty search query');
        throw new Error('Search query cannot be empty');
      }

      const endpoint = '/youtube/discovery/search/';
      const params = { query };
      
      console.log(`[YouTube API] Sending search request to ${endpoint} with query: "${query}"`);
      const startTime = performance.now();
      
      const response = await apiClient.get<YoutubeVideoListResponse>(endpoint, { params });
      
      const duration = (performance.now() - startTime).toFixed(2);
      const resultCount = Array.isArray(response.data) ? response.data.length : 'unknown';
      console.log(`[YouTube API] Search completed in ${duration}ms, received ${resultCount} results for query: "${query}"`);
      
      return response.data;
    } catch (error) {
      console.error('[YouTube API] Search request failed:', error);
      throw error;
    }
  },

  async getQueue(): Promise<YoutubeQueue> {
    try {
      const response = await apiClient.get<YoutubeQueueData>('/youtube/queue/');
      return new YoutubeQueue(apiClient, response.data);
    } catch (error) {
      console.error('Failed to fetch YouTube queue:', error);
      throw error;
    }
  }
};
